import React from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { RxCross2 } from "react-icons/rx";

import {
  BedIcon,
  AreaIcon,
  BathIcon,
  CrossIcon,
  HeartFillIcon,
  HeartEmptyIcon,
  RightArrowIcon,
  PinLocationIcon,
} from "../../../assets/icons";
import { convertCurrency, numberFormatting } from "../../utility";
import RoundedButton from "../buttons/RoundedButton";
import { PROPERTY_IMAGE_TYPES } from "../../../system";
import { propertyActions } from "../../../redux/actions";
import { areaMeasurmentOptions } from "../../../constants";

const PropertyCard = ({
  property,
  makePropertyFavourite,
  removePropertyFromFavourite,
  refetch = () => {},
  addToNotInterestedProperty,
  allCurrencies,
  defaultCurrency,
}) => {
  const navigate = useNavigate();

  const navigateToPropertyDetailPage = (propertyId) => {
    const path = `/property-detail/${propertyId}`;
    navigate(path);
  };

  const onUpdateFavourites = (id, wasFavorite) => {
    if (wasFavorite) {
      removePropertyFromFavourite(id, refetch);
    } else {
      makePropertyFavourite(id, refetch);
    }
  };

  const defaultCurrencyObject = allCurrencies.find(
    (currency) => currency.id === Number(defaultCurrency)
  );

  const getConvertedValue = convertCurrency(
    property.price,
    property.currency.code,
    defaultCurrencyObject,
    allCurrencies
  );

  const getAreaLabel = (area) => {
    return (
      <div>
        {areaMeasurmentOptions.find((type) => type.value === area)?.unit}
        <sup>2</sup>
      </div>
    );
  };

  return (
    <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4 col-xl-3">
      <div className="property-card">
        <div className="card position-relative">
          {(!property.status || !property.agentSubscriptionStatus) && (
            <div className="inactive-div d-flex align-items-center justify-content-center flex-column gap-2">
              <div className="inactive-div-text">
                This property is no longer available.
              </div>
              <RoundedButton
                className="inactive-div-button py-2 d-flex align-items-center gap-2"
                onClick={() =>
                  removePropertyFromFavourite(property.id, refetch)
                }
              >
                <RxCross2 className="inactive-div-cross-icon" />
                <div>Remove from Favourites</div>
              </RoundedButton>
            </div>
          )}
          {/* Image with icons */}
          <div className="card-image-wrapper">
            <img
              alt="Apartment"
              src={
                property.images?.find(
                  (img) => img.type === PROPERTY_IMAGE_TYPES.COVER_IMAGE
                )?.url ?? property.images[0]?.url
              }
              className="card-img-top"
            />
            {/* Heart and close icons */}
            <img
              src={property?.isFavourite ? HeartFillIcon : HeartEmptyIcon}
              alt="favorite icon"
              className="favorite-icon pointer"
              onClick={() =>
                onUpdateFavourites(property.id, property?.isFavourite)
              }
            />
            <img
              src={CrossIcon}
              alt="cross icon"
              className="close-icon"
              onClick={() => addToNotInterestedProperty(property.id, refetch)}
            />
          </div>
          {/* Card body */}
          <div className="card-body">
            {/* Row with Price and View button */}
            <div className="d-flex justify-content-between align-items-center">
              {/* Price */}
              <p className="card-title mb-0">
                <div className="d-flex gap-1">
                  <div>{defaultCurrencyObject?.symbol}</div>
                  <div>{numberFormatting(getConvertedValue)}</div>
                </div>
              </p>
              {/* Button to view more */}
              <button
                className="view-btn btn btn-primary rounded-pill"
                onClick={() => navigateToPropertyDetailPage(property.id)}
              >
                <img src={RightArrowIcon} alt="right Arrow" />
              </button>
            </div>
            {/* Title */}
            <p className="text-ellipsis card-subtitle" title={property?.title}>
              {property?.title}
            </p>
            {/* Address */}
            <div className="card-address mb-2">
              <img src={PinLocationIcon} alt="location icon" className="me-2" />
              <span
                className="text-ellipsis"
                title={property?.location?.address}
              >
                {property?.location?.address}
              </span>
            </div>
            {/* Property details */}
            <div className="property-details d-flex justify-content-flex-start align-items-center mt-2">
              <div>
                <img src={BedIcon} alt="bed icon" className="me-2" />
                {property?.rooms === 0
                  ? "Studio"
                  : property?.rooms === 10
                  ? "10+"
                  : property?.rooms}
              </div>
              <div>
                <img src={BathIcon} alt="bed icon" className="me-2" />
                {property?.baths}
              </div>
              <div>
                <img src={AreaIcon} alt="bed icon" className="me-2" />
                {property?.area}{" "}
                <span>{getAreaLabel(property?.areaMeasurementType)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  allCurrencies: state.homeReducer.allCurrencies,
  defaultCurrency: state.homeReducer.defaultCurrency,
});

const mapDispatchToProps = (dispatch) => ({
  makePropertyFavourite: (id, func) =>
    dispatch(propertyActions.makePropertyFavourite(id, func)),
  removePropertyFromFavourite: (id, func) =>
    dispatch(propertyActions.removePropertyFromFavourite(id, func)),
  addToNotInterestedProperty: (id, func) =>
    dispatch(propertyActions.addToNotInterestedProperty(id, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PropertyCard);
